import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { cardPhotoUrl, isEmail, normalizeDigits, photoUrl } from "@1-now/booking-core";
import { isNotBookable } from "utils/carPrice";
import { trackBookingStarted } from "analytics/bookingEvents";
import type { PhoneBookingProps } from "./PhoneBooking";
import type { PayDraft } from "designs/Common/CheckOutPage/CheckOutPage";
import { PAY, PayChoice, REVIEW, Step } from "./steps";
import type { ProofFile } from "./ProofUpload";
import { tripError } from "./tripError";
import { tripStops } from "./tripStops";

/**
 * Everything the booking in steps needs from the car page: the phone's sheet (1NOW-11155) and the desktop's
 * pages (1NOW-11173), which are two renderers of one flow. Both car pages (CarListingDetailPage and
 * ListingCarDetailPage) pass their own state and handlers in; the checks are the ones their booking buttons
 * make, and the booking itself is made by their own handleBooking / handleBookingLater.
 */
export interface PhoneBookingContext {
  isPhone: boolean;
  /** 1024px and up. Neither this nor isPhone is true where the browser cannot say, and the page stays the old one. */
  isDesktop: boolean;
  /** The page's long-term view has its own sidebar and keeps it. */
  longTermView: boolean;

  open: boolean;
  /** `replace`: on desktop, the address is corrected rather than a place the renter went to. */
  setOpen: (open: boolean, replace?: boolean) => void;
  step: Step;
  setStep: (step: Step, replace?: boolean) => void;
  madeBooking: any;
  setMadeBooking: (booking: any) => void;

  car: any;
  ourCompany: any;
  companyDetails: any;
  invoice: any;
  bookingdetails: any;

  pickupLocation: any;
  dropOffLocation: any;
  sameDropoffLocation: boolean;
  dropoffLocationType: boolean;
  startDate: Date | null;
  endDate: Date | null;
  startTime?: string | null;
  endTime?: string | null;

  products: any[];
  productsLoading: boolean;
  /** A reason some packages are missing (all booking days are in the past). */
  protectionNotice?: string;
  selectedBonzahType: any;
  bonzahProducts: any;
  onSelectProduct: (product: any) => void;
  proofFiles: ProofFile[];
  setProofFiles: Dispatch<SetStateAction<ProofFile[]>>;
  proofInputRef: RefObject<HTMLInputElement>;

  addons: any[];
  selectedExtras: Set<any>;
  onExtra: (id: any, on: boolean) => void;
  selectedAddons: Set<any>;
  onAddon: (value: string) => void;

  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  setEmail: (v: string) => void;
  setPhone: (v: string) => void;
  errors: { [key: string]: string };
  clearError: (field: string) => void;
  validateForm: () => boolean;
  renterDetailsConfirmed: boolean;
  setRenterDetailsConfirmed: (v: boolean) => void;

  handleBooking: () => void;
  handleBookingLater: () => void;
  loading: boolean;
  loadingLater: boolean;
}

export const usePhoneBooking = (c: PhoneBookingContext) => {
  const phoneSteps = c.isPhone && !isNotBookable(c.car) && !c.longTermView;
  // Long-term rentals and cars with no price keep today's page, on every width.
  const desktopSteps = c.isDesktop && !isNotBookable(c.car) && !c.longTermView;
  const madeKey = useRef("");
  /** The choices the review last passed (the booking made or kept for them): Pay opens only for these. */
  const reviewedKey = useRef("");
  const navigate = useNavigate();
  /** The consent line of the Pay step (1NOW-11205): the Rental Agreement and policies, accepted. Kept here, so a swap between the phone and desktop layouts does not lose it. */
  const [accepted, setAccepted] = useState(false);
  /** Pay now or pay later, answered by the review's button (1NOW-11205): the booking is made the way it says. */
  const [payChoice, setPayChoice] = useState<PayChoice>("now");
  /** The same answer, read at once: the page's booking call (and its onBooked) is the click's render, before the state is. */
  const choice = useRef<PayChoice>("now");
  /** What the renter typed on the Pay step (billing address, a promo code not yet applied): kept here, above the phone/desktop swap (1NOW-11197). */
  const [payDraft, setPayDraft] = useState<PayDraft>({});
  /** The reservation, not the card payment: only a car that allows pay later can be asked for it. */
  const later = c.car?.pay_later === true && payChoice === "later";
  const laterNow = () => c.car?.pay_later === true && choice.current === "later";
  /** This page opened the steps itself: an address that carries a step is not a way in (opened directly, reloaded, or Back from the payment's own page). */
  const entered = useRef(false);
  /** Sent to a step by the review's Edit: its button says "Back to review" and goes straight back to it. */
  const [returning, setReturning] = useState(false);
  const editStep = useRef<Step | null>(null);
  /** The trip's Edit took the renter out of the review (or past it): "Book this car" opens it at the review again. */
  const toReview = useRef(false);

  /** Everything a booking made now would be made from: if it is unchanged, the booking already made still stands. */
  const choicesKey = () =>
    JSON.stringify([
      c.car?.id,
      c.startDate,
      c.endDate,
      c.startTime,
      c.endTime,
      c.pickupLocation?.id,
      c.dropOffLocation?.id,
      c.sameDropoffLocation,
      Array.from(c.selectedExtras).sort(),
      c.selectedBonzahType,
      Array.from(c.selectedAddons).sort(),
      c.firstName,
      c.lastName,
      c.email,
      c.phone,
      // A reservation and a card booking are different bookings: the other choice is another booking.
      laterNow(),
    ]);

  const trip = () => ({
    pickupLocation: c.pickupLocation,
    sameDropoffLocation: c.sameDropoffLocation,
    dropOffLocation: c.dropOffLocation,
    startDate: c.startDate,
    endDate: c.endDate,
    startTime: c.startTime,
    endTime: c.endTime,
  });

  /**
   * The page's own handleBooking (card) or handleBookingLater (reservation) has made the booking from the review
   * while the steps are open: the renter lands on Pay with it. A new booking is a new filled-in agreement, so the
   * consent is asked again.
   */
  const onBooked = (booking: any) => {
    c.setMadeBooking(booking);
    madeKey.current = choicesKey();
    reviewedKey.current = madeKey.current;
    setAccepted(false);
    c.setStep(PAY);
  };

  const protectionProblem = (): string | null => {
    if (!c.selectedBonzahType || c.selectedBonzahType.length === 0) return "Please select a trip insurance option";
    const chosen = c.bonzahProducts?.insurance_options?.find(
      (p: any) => JSON.stringify(p.value) === JSON.stringify(c.selectedBonzahType),
    );
    if (chosen?.upload_before_payment === true && c.proofFiles.length === 0)
      return "Please upload proof of insurance before continuing.";
    return null;
  };
  const checkProtection = () => {
    const problem = protectionProblem();
    if (problem) toast.error(problem);
    return !problem;
  };

  /** The checks handleBooking makes before it asks the server for anything, with its messages. */
  const detailsProblem = (): string | null =>
    tripError(trip()) ??
    (!c.validateForm()
      ? "Please fill in the details"
      : c.companyDetails?.show_renter_confirmation_checkbox && !c.renterDetailsConfirmed
        ? "Please confirm that your renter details are correct"
        : null);
  const detailsOk = () => {
    const problem = detailsProblem();
    if (problem) toast.error(problem);
    return !problem;
  };

  /**
   * Where "Book this car" opens the steps: the first, or the review when the renter came out of it for the trip
   * and what they filled in still holds. What no longer holds (the protection no longer offered for the new
   * dates, say) sends them to the first step that needs them.
   */
  const stepToOpen = (): { step: Step; problem: string | null } => {
    if (!toReview.current) return { step: 1, problem: null };
    const details = detailsProblem();
    if (details) return { step: 1, problem: details };
    const offered =
      c.products.length === 0 ||
      c.products.some((p: any) => JSON.stringify(p.value) === JSON.stringify(c.selectedBonzahType));
    const protection = protectionProblem() ?? (offered ? null : "Please select a trip insurance option");
    return protection ? { step: 2, problem: protection } : { step: 4, problem: null };
  };

  const onBookThisCar = () => {
    const missing = tripError(trip());
    if (missing) {
      toast.error(missing);
      // Take the renter to where the trip is picked, or the button just looks dead.
      document.getElementById("trip-details")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      return;
    }
    const target = stepToOpen();
    toReview.current = false;
    if (target.problem) toast.error(target.problem);
    entered.current = true;
    setReturning(false);
    c.setStep(target.step);
    c.setOpen(true);
  };

  /** The review's Edit on Details, Protection or Extras: go there; its button comes straight back to the review. */
  const onEditStep = (step: 1 | 2 | 3) => {
    editStep.current = step;
    setReturning(true);
    c.setStep(step);
  };

  /**
   * The review's "Pay and book now" or "Pay later": the booking is made here the way the renter chose (card, or reservation for a
   * car that allows pay later), then Pay; or an earlier one for exactly these choices stands. Another choice (the extras,
   * or pay now instead of pay later) is another booking: the earlier one is left as it was, as it always has been
   * (the server has no way to change a booking's pay-later flag, and cancelling it from here would mail the operator).
   * A pending, unpaid booking holds the car for no one.
   */
  const onContinueFromReview = (next: PayChoice) => {
    choice.current = next;
    setPayChoice(next);
    const same = Boolean(c.madeBooking) && madeKey.current === choicesKey();
    if (c.madeBooking && !same) c.setMadeBooking(null);
    if (same) {
      reviewedKey.current = choicesKey();
      return c.setStep(PAY);
    }
    if (laterNow()) c.handleBookingLater();
    else c.handleBooking();
  };

  /**
   * The Pay step's "Reserve" (pay later): the reservation exists. The renter sees it confirmed on the success page,
   * which counts down to its booking page (1NOW-11198). Its uuid is in the address (a reload keeps it), and the
   * booking itself goes along so the page has it at once.
   */
  const onReserve = () => {
    const id = c.madeBooking?.unique_identifier;
    if (id) navigate(`/payment-success?reserved=${encodeURIComponent(id)}`, { state: { booking: c.madeBooking } });
  };

  /** The trip is chosen on the car page, behind the sheet: close it and scroll to where it is. */
  const onEditTrip = () => {
    toReview.current = c.step >= REVIEW;
    setReturning(false);
    c.setOpen(false);
    window.setTimeout(() => document.getElementById("trip-details")?.scrollIntoView?.({ block: "start" }), 60);
  };

  const ready = tripError(trip()) === null;

  /** Whichever layout is on screen: the steps are the same flow. */
  const stepsOn = phoneSteps || desktopSteps;
  const open = c.open && stepsOn && ready;

  // The steps are an address (`?step=`) on every width. One this page did not open (typed in, reloaded, or Back
  // from the payment's own page) has nothing behind it, so it is the car page again; and Forward must not reach
  // Pay for choices the review has not passed.
  useEffect(() => {
    // Back to the review only for the step the Edit sent them to: any other way out (the stepper, Back, closing) is the normal order.
    if (returning && (!c.open || c.step !== editStep.current)) setReturning(false);
  });
  useEffect(() => {
    if (!stepsOn || !c.open) return;
    if (!entered.current || !ready) c.setOpen(false, true);
    else if (c.step === PAY && reviewedKey.current !== choicesKey()) c.setStep(REVIEW, true);
  });

  // The funnel's one booking event, from here so it is the same in both layouts and once per booking: the Pay
  // step shows a card booking that now exists (a reservation reports itself where the page creates it).
  const announced = useRef<unknown>(null);
  useEffect(() => {
    const id = c.madeBooking?.id;
    if (!open || c.step !== PAY || later || id === undefined || announced.current === id) return;
    announced.current = id;
    trackBookingStarted(c.madeBooking, c.car, c.ourCompany?.currency);
  });

  const props: PhoneBookingProps = {
    open,
    setOpen: (open: boolean, replace?: boolean) => {
      if (!open) setReturning(false);
      c.setOpen(open, replace);
    },
    step: c.step,
    setStep: (step: Step, replace?: boolean) => {
      setReturning(false);
      c.setStep(step, replace);
    },
    returning,
    onEditStep,
    accepted,
    setAccepted,
    payChoice,
    onReserve,
    payDraft,
    setPayDraft,
    car: c.car,
    photo: cardPhotoUrl(c.car) ?? photoUrl(c.car?.fleet_photos?.[0]?.photo_url),
    ready,
    invoice: c.invoice,
    bookingdetails: c.bookingdetails,
    madeBooking: c.madeBooking,
    trip: tripStops({
      pickupLocation: c.pickupLocation,
      dropOffLocation: c.dropOffLocation,
      dropoffLocationType: c.dropoffLocationType,
      startDate: c.startDate,
      endDate: c.endDate,
      ourCompany: c.ourCompany,
      tripDays: c.invoice?.trip_days,
    }),
    pickUp: c.startDate,
    companyDetails: c.companyDetails,
    companyName: c.companyDetails?.company_name,
    protection: {
      products: c.products,
      loading: c.productsLoading,
      notice: c.protectionNotice,
      selectedValue: c.selectedBonzahType,
      onSelect: c.onSelectProduct,
      proof: { files: c.proofFiles, setFiles: c.setProofFiles, inputRef: c.proofInputRef },
    },
    extras: {
      extras: c.car?.extras ?? [],
      addons: c.addons,
      selectedExtras: c.selectedExtras,
      onExtra: c.onExtra,
      selectedAddons: c.selectedAddons,
      onAddon: c.onAddon,
    },
    details: {
      fields: {
        firstName: {
          value: c.firstName,
          error: c.errors.firstName,
          onChange: (v) => {
            c.setFirstName(v);
            c.setRenterDetailsConfirmed(false);
            if (v) c.clearError("firstName");
          },
        },
        lastName: {
          value: c.lastName,
          error: c.errors.lastName,
          onChange: (v) => {
            c.setLastName(v);
            c.setRenterDetailsConfirmed(false);
            if (v) c.clearError("lastName");
          },
        },
        email: {
          value: c.email,
          error: c.errors.email,
          onChange: (v) => {
            c.setEmail(v);
            if (isEmail(v)) c.clearError("email");
          },
        },
        phone: {
          value: c.phone,
          error: c.errors.phone,
          onChange: (v) => {
            c.setPhone(v);
            c.setRenterDetailsConfirmed(false);
            if (normalizeDigits(v).length >= 10) c.clearError("phone");
          },
        },
      },
      confirm: c.companyDetails?.show_renter_confirmation_checkbox
        ? {
            checked: c.renterDetailsConfirmed,
            onChange: (checked) => {
              if (!c.validateForm()) {
                toast.error("Please fill in your renter details above before confirming");
                return;
              }
              c.setRenterDetailsConfirmed(checked);
            },
            html: c.companyDetails?.renter_confirmation_checkbox_text,
            companyName: c.companyDetails?.company_name,
          }
        : undefined,
    },
    onBookThisCar,
    checkProtection,
    checkDetails: detailsOk,
    onContinueFromReview,
    onEditTrip,
    busy: c.loading,
    busyLater: c.loadingLater,
    payLater: c.car?.pay_later === true,
  };

  return { phoneSteps, desktopSteps, props, onBooked };
};
